// client/src/pages/VendorDetailPage.tsx
//
// QA 9 Oct retest item 36: the Vendor List had no detail page at all — clicking a
// vendor did nothing. This page reuses existing owners for each section rather than
// computing anything of its own:
//   - header fields: `getVendor` (lib/vendorsApi), the same `?id=` read the vendor
//     item-memory panel already uses (USC-BUG-0031) — no new vendor-by-id endpoint.
//   - balance: `getSupplierBalances` (lib/accountsPayableReportsApi ->
//     src/services/apReports.service.js), the one "what we owe each supplier" owner
//     behind the Accounts module's own supplier-balances report. A bill carries only
//     `supplierName` (matched BY NAME, same as that report), so the vendor's exact
//     name is matched out of the `q`-filtered rows rather than re-deriving anything.
//     Gated on the `financials` permission that route itself requires.
//   - invoices / purchase orders: `listVendorInvoices` / `fetchVendorPurchaseOrders`,
//     the same list calls the Vendor Invoices and Vendor Purchase Orders pages use,
//     scoped to this vendor by name through the search params those lists already
//     accept (`q` / `search`, both regex-matched against vendor name server-side).
import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import LoadingState from '../components/LoadingState';
import StatusBadge from '../components/StatusBadge';
import { ErrorState } from '../components/ui/States';
import { usePermission } from '../hooks/usePermission';
import { getVendor, type VendorSearchResult } from '../lib/vendorsApi';
import { getSupplierBalances, type SupplierBalanceRow } from '../lib/accountsPayableReportsApi';
import { listVendorInvoices, type VendorInvoice } from '../lib/vendorInvoiceApi';
import { fetchVendorPurchaseOrders, type VendorPurchaseOrderDto } from '../lib/vendorPurchaseOrderApi';
import { formatCurrency } from '../utils/priceUtils';
import { formatDate } from '../utils/dateFormat';
import { creditDaysOnFile } from '../utils/paymentTerms';
import { normalizeErrorMessage } from '../utils/errorPresentation';

type BalanceRow = SupplierBalanceRow & { currency: string };

export const VendorDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const financialsPerm = usePermission('financials');
  const invoicePerms = usePermission('invoices');
  const poPerms = usePermission('vendor_purchase_orders');

  const [vendor, setVendor] = useState<VendorSearchResult | null>(null);
  const [vendorLoading, setVendorLoading] = useState(true);
  const [vendorError, setVendorError] = useState<string | null>(null);

  const [balanceRows, setBalanceRows] = useState<BalanceRow[] | null>(null);
  const [balanceError, setBalanceError] = useState<string | null>(null);

  const [invoices, setInvoices] = useState<VendorInvoice[] | null>(null);
  const [invoicesError, setInvoicesError] = useState<string | null>(null);

  const [purchaseOrders, setPurchaseOrders] = useState<VendorPurchaseOrderDto[] | null>(null);
  const [posError, setPosError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    let active = true;
    setVendorLoading(true);
    getVendor(id)
      .then((v) => {
        if (!active) return;
        setVendor(v);
        setVendorError(v ? null : 'This vendor could not be found.');
      })
      .catch((err) => {
        if (active) setVendorError(normalizeErrorMessage(err));
      })
      .finally(() => {
        if (active) setVendorLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id]);

  // Balance: matched BY NAME, same rule the Accounts supplier-balances report itself
  // uses (a bill carries only `supplierName`), so a vendor billed under a slightly
  // different spelling on some invoices will not all land here — that is the report's
  // own documented limit, not something this page should second-guess.
  useEffect(() => {
    if (!vendor?.name || !financialsPerm.canView) return;
    let active = true;
    getSupplierBalances({ q: vendor.name })
      .then((res) => {
        if (!active) return;
        const wanted = vendor.name.trim().toLowerCase();
        const rows: BalanceRow[] = [];
        for (const block of res?.data?.currencies || []) {
          for (const row of block.suppliers) {
            if (row.supplierName.trim().toLowerCase() === wanted) {
              rows.push({ ...row, currency: block.currency });
            }
          }
        }
        setBalanceRows(rows);
        setBalanceError(null);
      })
      .catch((err) => {
        if (active) setBalanceError(normalizeErrorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [vendor?.name, financialsPerm.canView]);

  useEffect(() => {
    if (!vendor?.name || !invoicePerms.canView) return;
    let active = true;
    listVendorInvoices({ q: vendor.name, limit: 25 })
      .then((res) => {
        if (active) {
          setInvoices(res.items || []);
          setInvoicesError(null);
        }
      })
      .catch((err) => {
        if (active) setInvoicesError(normalizeErrorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [vendor?.name, invoicePerms.canView]);

  useEffect(() => {
    if (!vendor?.name || !poPerms.canView) return;
    let active = true;
    fetchVendorPurchaseOrders(1, 25, vendor.name)
      .then((data) => {
        if (active) {
          setPurchaseOrders(data.vendorPurchaseOrders || []);
          setPosError(null);
        }
      })
      .catch((err) => {
        if (active) setPosError(normalizeErrorMessage(err));
      });
    return () => {
      active = false;
    };
  }, [vendor?.name, poPerms.canView]);

  const backButton = (
    <button
      type="button"
      onClick={() => navigate('/vendors')}
      className="p-1 rounded-md hover:bg-slate-200/80 text-slate-900"
      aria-label="Back to vendor list"
    >
      <ArrowLeft className="w-5 h-5" />
    </button>
  );

  if (vendorLoading) {
    return (
      <div className="h-full flex flex-col bg-brand-bg">
        <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8 flex items-center gap-2">
          {backButton}
          <h1 className="text-xl font-semibold leading-7 text-slate-900">Vendor</h1>
        </div>
        <div className="flex-1 overflow-auto px-4 py-6 sm:px-8">
          <LoadingState text="Loading vendor..." />
        </div>
      </div>
    );
  }

  if (!vendor) {
    return (
      <div className="h-full flex flex-col bg-brand-bg">
        <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8 flex items-center gap-2">
          {backButton}
          <h1 className="text-xl font-semibold leading-7 text-slate-900">Vendor</h1>
        </div>
        <div className="flex-1 overflow-auto px-4 py-6 sm:px-8">
          <ErrorState message={vendorError || 'This vendor could not be found.'} />
        </div>
      </div>
    );
  }

  const creditDays = creditDaysOnFile(vendor.creditDays);

  return (
    <div className="h-full flex flex-col bg-brand-bg">
      <div className="shrink-0 border-b border-slate-200 bg-brand-bg px-4 pb-3 pt-4 sm:px-8">
        <div className="flex items-center gap-2">
          {backButton}
          <h1 className="text-xl font-semibold leading-7 text-slate-900">{vendor.name}</h1>
        </div>
        <dl className="mt-2 grid grid-cols-1 gap-x-6 gap-y-1 text-sm text-slate-600 sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Email</dt>
            <dd>{vendor.email || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Contact</dt>
            <dd>{vendor.phone || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Address</dt>
            <dd>{vendor.address || '-'}</dd>
          </div>
          <div>
            <dt className="text-xs uppercase tracking-wide text-slate-400">Credit days</dt>
            <dd>{creditDays === null ? 'Not on file' : `${creditDays} days`}</dd>
          </div>
        </dl>
      </div>

      <div className="flex-1 overflow-auto px-4 py-4 sm:px-8 space-y-4">
        {/* Balance */}
        <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <h2 className="px-4 py-2 text-sm font-semibold text-slate-900 border-b border-slate-200">Balance</h2>
          <div className="p-4">
            {!financialsPerm.canView ? (
              <p className="text-sm text-slate-500">You do not have permission to view vendor balances.</p>
            ) : balanceError ? (
              <ErrorState message={balanceError} />
            ) : balanceRows === null ? (
              <LoadingState text="Loading balance..." />
            ) : balanceRows.length === 0 ? (
              <p className="text-sm text-slate-500">No open balance on file for this vendor.</p>
            ) : (
              <table className="data-table data-table-grid">
                <thead>
                  <tr>
                    <th>Currency</th>
                    <th>Owed</th>
                    <th>Overdue</th>
                    <th>Open credit</th>
                    <th>Net owed</th>
                  </tr>
                </thead>
                <tbody>
                  {balanceRows.map((row) => (
                    <tr key={row.currency}>
                      <td>{row.currency}</td>
                      <td>{formatCurrency(row.owed, row.currency, { ceil: false })}</td>
                      <td>{formatCurrency(row.overdue, row.currency, { ceil: false })}</td>
                      <td>{formatCurrency(row.openCredit, row.currency, { ceil: false })}</td>
                      <td>{formatCurrency(row.netOwed, row.currency, { ceil: false })}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Invoices */}
        <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <h2 className="px-4 py-2 text-sm font-semibold text-slate-900 border-b border-slate-200">Invoices</h2>
          <div className="p-4">
            {!invoicePerms.canView ? (
              <p className="text-sm text-slate-500">You do not have permission to view vendor invoices.</p>
            ) : invoicesError ? (
              <ErrorState message={invoicesError} />
            ) : invoices === null ? (
              <LoadingState text="Loading invoices..." />
            ) : invoices.length === 0 ? (
              <p className="text-sm text-slate-500">No invoices on file for this vendor.</p>
            ) : (
              <table className="data-table data-table-grid">
                <thead>
                  <tr>
                    <th>Invoice</th>
                    <th>Date</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv) => (
                    <tr key={inv._id}>
                      <td>{(inv as any)?.extracted?.invoice?.invoiceNumber || inv?.source?.fileName || '-'}</td>
                      <td className="whitespace-nowrap">{formatDate(inv.createdAt, '-')}</td>
                      <td><StatusBadge status={inv.status} /></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>

        {/* Purchase orders */}
        <section className="bg-white border border-slate-200 rounded-md overflow-hidden">
          <h2 className="px-4 py-2 text-sm font-semibold text-slate-900 border-b border-slate-200">Purchase orders</h2>
          <div className="p-4">
            {!poPerms.canView ? (
              <p className="text-sm text-slate-500">You do not have permission to view vendor purchase orders.</p>
            ) : posError ? (
              <ErrorState message={posError} />
            ) : purchaseOrders === null ? (
              <LoadingState text="Loading purchase orders..." />
            ) : purchaseOrders.length === 0 ? (
              <p className="text-sm text-slate-500">No purchase orders on file for this vendor.</p>
            ) : (
              <table className="data-table data-table-grid">
                <thead>
                  <tr>
                    <th>PO number</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {purchaseOrders.map((po) => (
                    <tr key={po._id}>
                      <td>
                        <Link to={`/vendor-purchase-orders/${po._id}`} className="text-cyan-700 hover:underline">
                          {po.poNumber}
                        </Link>
                      </td>
                      <td className="whitespace-nowrap">{formatDate(po.createdAt, '-')}</td>
                      <td><StatusBadge status={po.status} /></td>
                      <td>{formatCurrency(po.totalPrice, po.currency || 'USD')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default VendorDetailPage;
